import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { useAiOperations, useAppletSystem } from './api'
import {
  CashflowV2Error,
  CashflowV2Loading,
  CashflowV2Unconfigured,
  looksUnconfigured,
} from './components/PageStates'
import { colors, fmt } from './utils'
import type { AiOperations, AppletSystem } from './types'

function KpiCard({
  label,
  value,
  subtext,
  color,
}: {
  label: string
  value: string
  subtext?: string
  color?: string
}) {
  return (
    <div className="rounded-xl bg-white shadow-sm border border-gray-100 p-5 flex flex-col gap-1 min-w-0 dark:bg-dark-surface dark:border-dark-border">
      <span className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
        {label}
      </span>
      <span
        className="text-2xl font-semibold truncate text-gray-900 dark:text-gray-100"
        style={color ? { color } : undefined}
      >
        {value}
      </span>
      {subtext && (
        <span className="text-xs text-gray-400 dark:text-gray-500 truncate">
          {subtext}
        </span>
      )}
    </div>
  )
}

function asNum(value: unknown, fallback = 0): number {
  const numeric = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(numeric) ? numeric : fallback
}

function asStr(value: unknown, fallback = ''): string {
  if (value == null) return fallback
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return fallback
}

function formatErrorRate(obs: Record<string, unknown>) {
  if (obs.error_rate_pct != null && obs.error_rate_pct !== '') {
    return `${asNum(obs.error_rate_pct).toFixed(1)}%`
  }
  const raw = obs.error_rate
  if (raw == null || raw === '') return '—'
  const numeric = asNum(raw)
  if (numeric >= 0 && numeric <= 1) return `${(numeric * 100).toFixed(1)}%`
  return `${numeric.toFixed(1)}%`
}

function observabilityKpis(obs: Record<string, unknown>) {
  const totalCalls = asNum(obs.total_calls ?? obs.total_llm_calls)
  const avgLatency = asNum(obs.avg_latency_ms)
  const totalCost = asNum(obs.total_cost_usd)
  let avgCostPerCall = asNum(obs.avg_cost_per_call)
  if (!avgCostPerCall && totalCalls > 0 && totalCost > 0) {
    avgCostPerCall = totalCost / totalCalls
  }
  return {
    totalCalls,
    avgLatency,
    avgCostPerCall,
    errorLabel: formatErrorRate(obs),
  }
}

function costByWorkflowChart(obs: Record<string, unknown>, workflows: Record<string, unknown>[]) {
  const fromObs = obs.cost_by_workflow
  const costMap = new Map<string, number>()
  if (fromObs && typeof fromObs === 'object' && !Array.isArray(fromObs)) {
    for (const [key, value] of Object.entries(fromObs as Record<string, unknown>)) {
      costMap.set(key, asNum(value))
    }
  }
  return workflows.map(workflow => {
    const workflowName = asStr(workflow.workflow_name ?? workflow.name, 'Workflow')
    const direct = asNum(workflow.total_cost)
    return { name: workflowName, total_cost: direct || costMap.get(workflowName) || 0 }
  })
}

function qualityTrendData(obs: Record<string, unknown>) {
  const raw = obs.quality_trend
  if (!Array.isArray(raw)) return []
  return raw.map(row => {
    const record = row as Record<string, unknown>
    return {
      period: asStr(record.period ?? record.week, ''),
      avg_quality: asNum(record.avg_quality),
    }
  })
}

function modelDistributionRows(obs: Record<string, unknown>) {
  const callsBy = obs.calls_by_model
  if (!callsBy || typeof callsBy !== 'object' || Array.isArray(callsBy)) return []
  const entries = Object.entries(callsBy as Record<string, unknown>).map(([model, count]) => ({
    model,
    count: asNum(count),
  }))
  const total = entries.reduce((sum, entry) => sum + entry.count, 0) || 1
  const totalCost = asNum(obs.total_cost_usd)
  return entries.map(entry => ({
    model: entry.model,
    pctOfCalls: (entry.count / total) * 100,
    cost: totalCost * (entry.count / total),
  }))
}

function workflowSteps(workflow: Record<string, unknown>) {
  const steps = workflow.steps
  if (Array.isArray(steps)) {
    return steps.map((step, index) => {
      const record = step as Record<string, unknown>
      return { label: asStr(record.name ?? record.step ?? record.label, `Step ${index + 1}`) }
    })
  }
  const agents = workflow.agents
  if (Array.isArray(agents)) {
    return agents.map((agent, index) => {
      const record = agent as Record<string, unknown>
      return { label: asStr(record.name, `Agent ${index + 1}`) }
    })
  }
  return []
}

function currentAppletName(current: AppletSystem['current_applet']) {
  if (current == null || current === '') return '—'
  if (typeof current === 'string') return current
  const record = current as Record<string, unknown>
  return asStr(record.name ?? record.applet_id, '—')
}

export default function AiOperationsPage() {
  const aiQuery = useAiOperations()
  const appletQuery = useAppletSystem()

  const ai: AiOperations | undefined = aiQuery.data
  const applet: AppletSystem | undefined = appletQuery.data

  const observability = useMemo(
    () =>
      ai?.observability_summary && typeof ai.observability_summary === 'object'
        ? (ai.observability_summary as Record<string, unknown>)
        : {},
    [ai?.observability_summary],
  )

  const promptRegistry = useMemo(
    () =>
      Array.isArray(ai?.prompt_registry)
        ? (ai?.prompt_registry as Record<string, unknown>[])
        : [],
    [ai?.prompt_registry],
  )

  const agentWorkflows = useMemo(
    () =>
      Array.isArray(ai?.agent_workflows)
        ? (ai?.agent_workflows as Record<string, unknown>[])
        : [],
    [ai?.agent_workflows],
  )

  const featureFlags = useMemo(
    () =>
      Array.isArray(applet?.feature_flags)
        ? (applet?.feature_flags as Record<string, unknown>[])
        : [],
    [applet?.feature_flags],
  )

  const customerConfig = useMemo(() => {
    const config = applet?.customer_config
    return config && typeof config === 'object' && !Array.isArray(config)
      ? (config as Record<string, unknown>)
      : {}
  }, [applet?.customer_config])

  const [flagOverrides, setFlagOverrides] = useState<Record<string, boolean>>({})
  const [optimizing, setOptimizing] = useState<Record<string, boolean>>({})

  useEffect(() => {
    setFlagOverrides(previous => {
      const next = { ...previous }
      let changed = false
      for (const flag of featureFlags) {
        const key = asStr(flag.flag)
        if (!key) continue
        if (typeof flag.value === 'boolean' && !(key in next)) {
          next[key] = flag.value
          changed = true
        }
      }
      return changed ? next : previous
    })
  }, [featureFlags])

  const toggleFlag = useCallback((flag: string) => {
    setFlagOverrides(previous => ({ ...previous, [flag]: !previous[flag] }))
  }, [])

  const handleOptimize = useCallback((promptId: string) => {
    setOptimizing(previous => ({ ...previous, [promptId]: true }))
    window.setTimeout(() => {
      setOptimizing(previous => ({ ...previous, [promptId]: false }))
    }, 2000)
  }, [])

  const kpis = useMemo(() => observabilityKpis(observability), [observability])
  const costData = useMemo(
    () => costByWorkflowChart(observability, agentWorkflows),
    [agentWorkflows, observability],
  )
  const qualityData = useMemo(
    () => qualityTrendData(observability),
    [observability],
  )
  const modelRows = useMemo(
    () => modelDistributionRows(observability),
    [observability],
  )

  const isLoading = aiQuery.isLoading || appletQuery.isLoading
  const firstError = aiQuery.error || appletQuery.error

  if (isLoading) {
    return <CashflowV2Loading label="Loading AI operations…" />
  }

  if (firstError) {
    if (looksUnconfigured(firstError)) {
      return <CashflowV2Unconfigured />
    }
    return (
      <CashflowV2Error
        title="Failed to load AI operations"
        description={(firstError as Error).message}
        onRetry={() => {
          aiQuery.refetch()
          appletQuery.refetch()
        }}
      />
    )
  }

  return (
    <div className="space-y-10">
      <header className="space-y-1">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          AI Operations
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          LLM observability, prompt management, and system configuration.
        </p>
      </header>

      <section className="space-y-5">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              Observability
            </h2>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              {asStr(observability.period, 'Current period')}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            label="Total LLM Calls"
            value={fmt.num(kpis.totalCalls)}
            subtext={`Error rate: ${kpis.errorLabel}`}
            color={colors.accent.teal}
          />
          <KpiCard
            label="Avg Latency"
            value={`${(kpis.avgLatency / 1000).toFixed(1)}s`}
            color={colors.accent.indigo}
          />
          <KpiCard
            label="Avg Cost / Call"
            value={`$${kpis.avgCostPerCall.toFixed(2)}`}
            color={colors.accent.gold}
          />
          <KpiCard
            label="Prompts"
            value={fmt.num(promptRegistry.length)}
            subtext={`${agentWorkflows.length} workflows`}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="rounded-xl bg-white dark:bg-dark-surface shadow-sm border border-gray-100 dark:border-dark-border p-5">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Cost by Workflow
            </h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={costData}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.chart.gridLine} vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: colors.text.secondary }} />
                <YAxis tick={{ fontSize: 11, fill: colors.text.secondary }} />
                <Tooltip formatter={(value: number) => `$${value.toFixed(2)}`} />
                <Bar dataKey="total_cost" fill={colors.accent.gold} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="rounded-xl bg-white dark:bg-dark-surface shadow-sm border border-gray-100 dark:border-dark-border p-5">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Quality Trend
            </h3>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={qualityData}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.chart.gridLine} vertical={false} />
                <XAxis dataKey="period" tick={{ fontSize: 11, fill: colors.text.secondary }} />
                <YAxis tick={{ fontSize: 11, fill: colors.text.secondary }} />
                <Tooltip formatter={(value: number) => `${Math.round(value * 100)}%`} />
                <Line
                  type="monotone"
                  dataKey="avg_quality"
                  stroke={colors.status.green}
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-xl bg-white dark:bg-dark-surface shadow-sm border border-gray-100 dark:border-dark-border overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 dark:border-dark-border">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Model Distribution
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500 border-b border-gray-100 dark:border-dark-border bg-gray-50/80 dark:bg-dark-surface-2">
                  <th className="py-3 px-4">Model</th>
                  <th className="py-3 px-4 text-right">% of calls</th>
                  <th className="py-3 px-4 text-right">Cost</th>
                </tr>
              </thead>
              <tbody>
                {modelRows.map(row => (
                  <tr key={row.model} className="border-b border-gray-50 dark:border-dark-border hover:bg-gray-50/60 dark:hover:bg-dark-surface-2/40">
                    <td className="py-2.5 px-4 font-medium text-gray-900 dark:text-gray-100">
                      {row.model}
                    </td>
                    <td className="py-2.5 px-4 text-right font-mono text-gray-700 dark:text-gray-200">
                      {row.pctOfCalls.toFixed(1)}%
                    </td>
                    <td className="py-2.5 px-4 text-right font-mono text-gray-700 dark:text-gray-200">
                      ${row.cost.toFixed(2)}
                    </td>
                  </tr>
                ))}
                {modelRows.length === 0 && (
                  <tr>
                    <td colSpan={3} className="py-6 text-center text-gray-400 dark:text-gray-500 text-sm">
                      No model telemetry available.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          Prompt Registry
        </h2>
        <div className="rounded-xl bg-white dark:bg-dark-surface shadow-sm border border-gray-100 dark:border-dark-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500 border-b border-gray-100 dark:border-dark-border bg-gray-50/80 dark:bg-dark-surface-2">
                  <th className="py-3 px-4">Prompt</th>
                  <th className="py-3 px-4">Model</th>
                  <th className="py-3 px-4 text-right">Tokens</th>
                  <th className="py-3 px-4 text-right">Latency</th>
                  <th className="py-3 px-4 text-right">Quality</th>
                  <th className="py-3 px-4 text-right">Version</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4 text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {promptRegistry.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-6 text-center text-gray-400 dark:text-gray-500 text-sm">
                      No prompts registered.
                    </td>
                  </tr>
                ) : (
                  promptRegistry.map(prompt => {
                    const promptId = asStr(prompt.prompt_id, asStr(prompt.name))
                    const quality = asNum(prompt.quality_score ?? prompt.avg_quality_score)
                    const qualityPct = quality > 0 && quality <= 1 ? quality * 100 : quality
                    const tokens = asNum(
                      prompt.avg_tokens ??
                        prompt.avg_token_count ??
                        (asNum(prompt.total_tokens_input) + asNum(prompt.total_tokens_output)),
                    )
                    const latency = asNum(prompt.avg_latency ?? prompt.avg_latency_ms)
                    const busy = !!optimizing[promptId]
                    return (
                      <tr
                        key={promptId}
                        className="border-b border-gray-50 dark:border-dark-border hover:bg-gray-50/60 dark:hover:bg-dark-surface-2/40"
                      >
                        <td className="py-2.5 px-4 font-medium text-gray-900 dark:text-gray-100">
                          {asStr(prompt.name, '—')}
                        </td>
                        <td className="py-2.5 px-4 text-gray-600 dark:text-gray-300">
                          {asStr(prompt.model, '—')}
                        </td>
                        <td className="py-2.5 px-4 text-right font-mono text-gray-700 dark:text-gray-200">
                          {tokens ? fmt.num(tokens) : '—'}
                        </td>
                        <td className="py-2.5 px-4 text-right font-mono text-gray-700 dark:text-gray-200">
                          {latency ? `${(latency / 1000).toFixed(1)}s` : '—'}
                        </td>
                        <td className="py-2.5 px-4 text-right font-mono text-gray-700 dark:text-gray-200">
                          {qualityPct ? `${qualityPct.toFixed(0)}%` : '—'}
                        </td>
                        <td className="py-2.5 px-4 text-right font-mono text-gray-600 dark:text-gray-300">
                          {prompt.version != null ? String(prompt.version) : '—'}
                        </td>
                        <td className="py-2.5 px-4">
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                              String(prompt.status).toLowerCase() === 'active'
                                ? 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-100'
                                : 'bg-amber-50 text-amber-800 ring-1 ring-amber-100'
                            }`}
                          >
                            {asStr(prompt.status, '—')}
                          </span>
                        </td>
                        <td className="py-2.5 px-4 text-center">
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => handleOptimize(promptId)}
                            className="inline-flex items-center justify-center rounded-lg border border-teal-200 bg-teal-50 px-2.5 py-1 text-[11px] font-semibold text-teal-800 hover:bg-teal-100 disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            {busy ? 'Optimizing...' : 'Optimize'}
                          </button>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          Agent Workflows
        </h2>
        <div className="space-y-4">
          {agentWorkflows.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500">
              No workflows configured.
            </p>
          ) : (
            agentWorkflows.map((workflow, index) => {
              const id = asStr(workflow.workflow_id ?? workflow.id, `workflow-${index}`)
              const title = asStr(workflow.workflow_name ?? workflow.name, 'Workflow')
              const description = asStr(workflow.description, '')
              const status = asStr(workflow.status, '—')
              const trigger = asStr(workflow.trigger ?? workflow.trigger_type ?? '—')
              const steps = workflowSteps(workflow)
              return (
                <article
                  key={id}
                  className="rounded-xl bg-white dark:bg-dark-surface shadow-sm border border-gray-100 dark:border-dark-border p-5 space-y-4"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="space-y-1 min-w-0">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {title}
                      </h3>
                      {description ? (
                        <p className="text-xs text-gray-500 dark:text-gray-400 max-w-3xl">
                          {description}
                        </p>
                      ) : null}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <span
                        className={`rounded-full px-2 py-0.5 font-semibold uppercase ${
                          status.toLowerCase() === 'active'
                            ? 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-100'
                            : 'bg-gray-100 text-gray-700 ring-1 ring-gray-200'
                        }`}
                      >
                        {status}
                      </span>
                      <span className="text-gray-400 dark:text-gray-500">Trigger</span>
                      <span className="font-mono text-gray-700 dark:text-gray-200 bg-gray-50 dark:bg-dark-surface-2 px-2 py-0.5 rounded border border-gray-100 dark:border-dark-border">
                        {trigger}
                      </span>
                    </div>
                  </div>
                  <div className="overflow-x-auto pb-1">
                    <div className="flex items-center gap-1 min-w-max py-1">
                      {steps.length === 0 ? (
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          No steps defined.
                        </span>
                      ) : (
                        steps.map((step, stepIndex) => (
                          <div key={`${id}-step-${stepIndex}`} className="flex items-center">
                            {stepIndex > 0 ? (
                              <span className="mx-1 text-gray-300 dark:text-gray-600 select-none" aria-hidden>
                                →
                              </span>
                            ) : null}
                            <div
                              className="rounded-lg border border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-surface-2 px-3 py-2 text-xs font-medium text-gray-800 dark:text-gray-100 shadow-sm whitespace-nowrap max-w-[160px] truncate"
                              title={step.label}
                            >
                              {step.label}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </article>
              )
            })
          )}
        </div>
      </section>

      <section className="space-y-5">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          Platform Configuration
        </h2>

        <div className="rounded-xl bg-white dark:bg-dark-surface shadow-sm border border-gray-100 dark:border-dark-border p-5">
          <h3 className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2">
            Current applet
          </h3>
          <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {currentAppletName(applet?.current_applet ?? '')}
          </p>
        </div>

        <div className="rounded-xl bg-white dark:bg-dark-surface shadow-sm border border-gray-100 dark:border-dark-border overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 dark:border-dark-border">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Feature Flags
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500 border-b border-gray-100 dark:border-dark-border bg-gray-50/80 dark:bg-dark-surface-2">
                  <th className="py-3 px-4">Flag</th>
                  <th className="py-3 px-4 w-28 text-center">Enabled</th>
                  <th className="py-3 px-4">Description</th>
                </tr>
              </thead>
              <tbody>
                {featureFlags.length === 0 ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="py-6 text-center text-gray-400 dark:text-gray-500 text-sm"
                    >
                      No feature flags.
                    </td>
                  </tr>
                ) : (
                  featureFlags.map(flag => {
                    const key = asStr(flag.flag)
                    const isBool = typeof flag.value === 'boolean'
                    const on = isBool ? flagOverrides[key] ?? flag.value : null
                    return (
                      <tr
                        key={key || JSON.stringify(flag)}
                        className="border-b border-gray-50 dark:border-dark-border hover:bg-gray-50/50 dark:hover:bg-dark-surface-2/40"
                      >
                        <td className="py-2.5 px-4 font-mono text-xs text-gray-800 dark:text-gray-100">
                          {key || '—'}
                        </td>
                        <td className="py-2.5 px-4 text-center">
                          {isBool ? (
                            <button
                              type="button"
                              role="switch"
                              aria-checked={on ?? false}
                              onClick={() => toggleFlag(key)}
                              className={`relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                                on ? 'bg-emerald-500' : 'bg-gray-200'
                              }`}
                            >
                              <span
                                className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow transition duration-200 ${
                                  on ? 'translate-x-5' : 'translate-x-0'
                                }`}
                              />
                            </button>
                          ) : (
                            <span className="font-mono text-xs text-gray-600 dark:text-gray-300">
                              {String(flag.value)}
                            </span>
                          )}
                        </td>
                        <td className="py-2.5 px-4 text-gray-600 dark:text-gray-300 text-xs">
                          {asStr(flag.description, '—')}
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-xl bg-white dark:bg-dark-surface shadow-sm border border-gray-100 dark:border-dark-border p-5">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Customer Configuration
          </h3>
          {Object.keys(customerConfig).length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500">
              No customer configuration.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(customerConfig).map(([key, value]) => (
                <div
                  key={key}
                  className="rounded-lg border border-gray-100 dark:border-dark-border bg-gray-50/60 dark:bg-dark-surface-2 p-3"
                >
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1 break-all">
                    {key}
                  </div>
                  <div className="text-sm text-gray-900 dark:text-gray-100 break-words">
                    {value != null && typeof value === 'object' ? (
                      <pre className="text-xs whitespace-pre-wrap font-mono text-gray-700 dark:text-gray-200 bg-white dark:bg-dark-surface rounded border border-gray-100 dark:border-dark-border p-2 max-h-40 overflow-auto">
                        {JSON.stringify(value, null, 2)}
                      </pre>
                    ) : (
                      <span className="font-mono text-gray-800 dark:text-gray-100">
                        {String(value)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  )
}
